import type { ButtonCustomNavigationData } from '@vedalib/editor/lib/elements'
import { ButtonNavigationAction } from '@vedalib/editor/lib/elements'
import { useCallback } from 'react'

import { useProjectT } from 'services/ProjectLabels'
import { showIncompleteElementList } from 'services/Store/Project/actions'
import { useProjectDispatch, useProjectContext } from 'services/Store/Project/hooks'
import { getProject } from 'services/Store/Project/selectors'
import { getNavigationLinkTrigger } from 'services/Store/Project/triggers'

import BaseButtonElement from '../BaseButtonElement'
import type { ButtonElementType } from '../buttonTypes'
import { focusMain } from 'utils/accessibility'

interface IDefaultButtonElementProps extends React.ComponentProps<ButtonElementType> {
  children: React.ReactNode
}

const NavigationButtonElement: React.FC<IDefaultButtonElementProps> = ({
  element,
  styles,
  mode,
  font,
  children,
}) => {
  const pt = useProjectT()
  const label = element?.value?.label || pt('elements.button.common.label')
  const showIncompleteModal = useProjectContext(getProject)?.displayIncompleteModal

  const { action, actionData } = element.value as {
    action: ButtonNavigationAction
    actionData: ButtonCustomNavigationData
  }
  const dispatch = useProjectDispatch()
  const trigger = useProjectContext(getNavigationLinkTrigger, {
    type: action,
    sectionId: actionData?.sectionId || '',
    blockId: actionData?.blockId,
  })
  const showModal = showIncompleteModal && action === ButtonNavigationAction.nextSection

  const onClick = useCallback(() => {
    if (showModal && trigger.disabled) {
      dispatch(showIncompleteElementList())
      return
    }

    dispatch(trigger.actionCreator())
    focusMain()
  }, [dispatch, trigger, showModal])

  return (
    <BaseButtonElement
      action={element.value.action}
      disabled={!showModal ? trigger.disabled : false}
      elementId={element.id}
      font={font}
      label={label}
      mode={mode}
      onClick={onClick}
      styles={styles}
    >
      {children}
    </BaseButtonElement>
  )
}

export default NavigationButtonElement
